import { cn } from '../../lib/shadcn/utils'

// The reconcile-derived filter buckets, shared by every queue tab. The km tab
// appends its own "unseen" / "seen" buckets on top of these.
export type QueueFilter =
  | 'all'
  | 'reconcile'
  | 'requested'
  | 'applied'
  | 'blocked'
  | 'unseen'
  | 'seen'

export const BASE_FILTERS: { id: QueueFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'reconcile', label: 'Reconcile available' },
  { id: 'requested', label: 'Requested' },
  { id: 'applied', label: 'Applied' },
  { id: 'blocked', label: 'Blocked' },
]

export const KM_FILTERS: { id: QueueFilter; label: string }[] = [
  ...BASE_FILTERS,
  { id: 'unseen', label: 'Unseen' },
  { id: 'seen', label: 'Seen' },
]

// Severity is a second, independent filter dimension that combines with the
// status filter above using AND. It is a three-way split. Minor is a window
// flip where no money moves. Material is a vehicle change that moves revenue
// between two owners' statements, while driver pay is unaffected. Unresolved
// means the scorer is confident the shift is wrong but cannot name where it
// belongs, so the revenue is still unattributed. These unresolved rows
// previously fell through to minor and were read as "no money moves", which was
// wrong: the money is not settled, it is merely unplaced.
export type SeverityFilter = 'all' | 'minor' | 'material' | 'unresolved'

export const SEVERITY_FILTERS: { id: SeverityFilter; label: string }[] = [
  { id: 'all', label: 'All severities' },
  { id: 'minor', label: 'Minor (no money moves)' },
  { id: 'material', label: 'Material (moves owner money)' },
  { id: 'unresolved', label: 'Unresolved (no destination yet)' },
]

// Whether a correction_type matches the selected severity. A null/absent type
// (e.g. a duplicate/mismatch row with no joined proposal) only matches "all
// severities": it is never counted as minor, material, or unresolved.
export function matchesSeverity(
  correctionType: string | null | undefined,
  severity: SeverityFilter,
): boolean {
  if (severity === 'all') return true
  return correctionType === severity
}

export function QueueFilters<T extends string>({
  filters,
  value,
  counts,
  onChange,
}: {
  filters: { id: T; label: string }[]
  value: T
  // Per-bucket row counts, keyed by filter id, so each chip can show a tally.
  counts: Partial<Record<T, number>>
  onChange: (next: T) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {filters.map((f) => {
        const active = value === f.id
        const count = counts[f.id]
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => onChange(f.id)}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              active
                ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                : 'border-border bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            {f.label}
            {typeof count === 'number' ? (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-[10px] tabular-nums',
                  active ? 'bg-background/60 text-foreground' : 'bg-muted text-muted-foreground',
                )}
              >
                {count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
